import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  FiLayers, FiSun, FiMoon, FiChevronDown, FiHome, FiZap, FiUser, FiBarChart2,
  FiSettings, FiCpu, FiDollarSign, FiSliders, FiShield, FiLogOut,
  FiUsers, FiAward, FiMessageSquare, FiList, FiPlus, FiClock,
  FiTarget, FiFolder, FiCheckSquare, FiCalendar, FiMessageCircle,
  FiActivity, FiUserCheck, FiGitPullRequest, FiTool, FiBookOpen, FiArchive, FiHeart,
  FiFileText, FiCompass,
} from 'react-icons/fi';
import { useDraftCount } from '../../hooks/useDrafts';
import { useQuery } from '@tanstack/react-query';
import { getVersion, hasUnseenVersion } from '../../services/versionService';
import NotificationIndicator from '../notifications/NotificationIndicator';
import NotificationsHistory from '../notifications/NotificationsHistory';
import branding from '../../branding.json';
import { BetaBadge } from '../common/BetaBadge';

// ── Shared dropdown item styles ────────────────────────────────────────────
// Builds menu item classes. gradient/hoverGradient are space-separated Tailwind classes.
// hover classes get individually prefixed with hover:
function menuItem(gradient: string, hoverGradient: string, border = true): string {
  const hoverClasses = hoverGradient.split(/\s+/).map(c => `hover:${c}`).join(' ');
  return [
    'block w-full text-left px-4 py-3 text-sm text-white',
    'bg-gradient-to-r', gradient,
    hoverClasses,
    border ? 'border-b border-white/10' : '',
    'transition-all duration-200 flex items-center gap-2',
  ].filter(Boolean).join(' ');
}

const menuSectionHeader = 'px-4 py-2 text-[10px] font-semibold text-gray-400 bg-gray-800/80 uppercase tracking-wider';

const menuItemDisabled = 'block w-full text-left px-4 py-3 text-sm bg-gradient-to-r from-gray-600/60 to-gray-700/60 opacity-50 cursor-not-allowed text-gray-400 flex items-center gap-2';

// ── Chevron ────────────────────────────────────────────────────────────────
const Chevron: React.FC<{ open?: boolean }> = ({ open }) => (
  <FiChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
);

export const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const { user, logout, openLoginModal } = useAuth();
  const { data: draftCount = 0 } = useDraftCount(!!user);

  // ── Dropdown states ────────────────────────────────────────────────────
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const personasRef = useRef<HTMLDivElement>(null);
  const artifactoryRef = useRef<HTMLDivElement>(null);
  const phasesRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const observatoryRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const refs: Record<string, React.RefObject<HTMLDivElement>> = {
    personas: personasRef, artifactory: artifactoryRef, phases: phasesRef,
    board: boardRef, observatory: observatoryRef, user: userRef,
  };

  const { data: versionInfo } = useQuery(['version'], getVersion, { staleTime: 300000 });
  const showWhatsNewBadge = versionInfo?.version ? hasUnseenVersion(versionInfo.version) : false;

  const isActive = (path: string) => location.pathname.startsWith(path);
  const isObservatoryActive = isActive('/dashboards') || isActive('/tools') || isActive('/skills');
  const isArtifactsActive = isActive('/artifacts') || isActive('/artifactory');
  const isBoardActive = isActive('/goals') || isActive('/projects') || isActive('/tasks') || isActive('/work') || isActive('/calendar');

  const toggle = useCallback((name: string) => {
    setOpenMenu(prev => prev === name ? null : name);
  }, []);

  const close = useCallback(() => setOpenMenu(null), []);

  // Close menus on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (openMenu && refs[openMenu]?.current && !refs[openMenu].current!.contains(e.target as Node)) {
        close();
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenu]);

  const getInitials = () => {
    if (!user) return '?';
    if (user.full_name) return user.full_name.split(' ').map((n: string) => n[0]).join('').toUpperCase().substring(0, 2);
    return user.username?.substring(0, 2).toUpperCase() || user.email?.substring(0, 2).toUpperCase() || '?';
  };

  return (
    <header className={`relative z-[200] ${darkMode ? 'bg-gray-900' : 'bg-gray-800'} text-white p-4 transition-colors duration-200`}>
      <div className="container mx-auto flex justify-between items-center">
        {/* ── Logo ──────────────────────────────────────────────────────── */}
        <Link to="/" className="flex items-center">
          {branding.logo?.enabled && (
            <div className="mr-3 overflow-hidden rounded-lg shadow-md" style={{ height: `${branding.logo.headerHeight || 40}px` }}>
              <img
                src={branding.logo.url || branding.logoUrl || '/logo.svg'}
                alt={branding.logo.alt || 'Logo'}
                className="h-full object-contain hover:opacity-90 transition-opacity duration-200"
                style={{ filter: 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.3))' }}
              />
            </div>
          )}
          <div className="flex flex-col">
            {(branding.header?.showAppName !== false) && (
              <span className="text-xl font-bold">{branding.appName}</span>
            )}
            {branding.header?.tagline && (
              <span className="text-sm text-gray-300">{branding.header.tagline}</span>
            )}
          </div>
        </Link>

        <div className="flex items-center">
          <nav className="flex items-center gap-1 mr-4">
            {user ? (
              <>
                {/* ── 0. Home ──────────────────────────────────────────── */}
                <Link
                  to="/"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors hover:bg-white/10 ${location.pathname === '/' ? 'text-white bg-white/10' : ''}`}
                >
                  <FiHome className="w-3.5 h-3.5" />
                  Home
                </Link>

                {/* ── 1. Personas ▾ ────────────────────────────────────── */}
                <div className="relative" ref={personasRef}>
                  <button
                    onClick={() => toggle('personas')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center transition-colors hover:bg-white/10 ${isActive('/personas') || isActive('/gymnasium') ? 'text-white bg-white/10' : ''}`}
                  >
                    Personas <Chevron open={openMenu === 'personas'} />
                  </button>
                  {openMenu === 'personas' && (
                    <div className="absolute left-0 mt-2 w-56 rounded-lg shadow-xl overflow-hidden z-[200] border border-white/10">
                      <Link to="/personas" className={menuItem('from-blue-600 to-blue-500', 'from-blue-700 to-blue-600')} onClick={close}>
                        <FiUsers className="w-4 h-4 shrink-0" /> Browse Personas
                      </Link>
                      <Link to="/personas/forge" className={menuItem('from-blue-500 to-purple-500', 'from-blue-600 to-purple-600')} onClick={close}>
                        <FiZap className="w-4 h-4 shrink-0" /> Persona Forge
                      </Link>
                      <Link to="/personas/draft-board" className={menuItem('from-rose-500 to-pink-500', 'from-rose-600 to-pink-600')} onClick={close}>
                        <FiFileText className="w-4 h-4 shrink-0" /> Drafts
                        {draftCount > 0 && (
                          <span className="ml-auto px-1.5 py-0.5 text-xs bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300 rounded-full">
                            {draftCount}
                          </span>
                        )}
                      </Link>
                      <Link to="/gymnasium" className={menuItem('from-purple-500 to-indigo-500', 'from-purple-600 to-indigo-600', false)} onClick={close}>
                        <FiAward className="w-4 h-4 shrink-0" /> Gymnasium
                      </Link>
                    </div>
                  )}
                </div>

                {/* ── 2. Conversations ─────────────────────────────────── */}
                <Link
                  to="/chat/sessions"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors hover:bg-white/10 ${isActive('/chat') ? 'text-white bg-white/10' : ''}`}
                >
                  <FiMessageSquare className="w-3.5 h-3.5" />
                  Conversations
                </Link>

                {/* ── 2b. Trailhead (experimental) ───────────────────── */}
                <Link
                  to="/trailhead"
                  className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors hover:bg-white/10 ${isActive('/trailhead') ? 'text-white bg-white/10' : ''}`}
                >
                  <FiCompass className="w-3.5 h-3.5" />
                  Trailhead
                  <span className="bg-amber-100 text-amber-800 text-xs font-mono px-1.5 py-0.5 rounded ml-1">
                    EXP
                  </span>
                </Link>

                {/* ── 3. Phases ▾ ─────────────────────────────────────── */}
                <div className="relative" ref={phasesRef}>
                  <button
                    onClick={() => toggle('phases')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center transition-colors hover:bg-white/10 ${isActive('/workflows') ? 'text-white bg-white/10' : ''}`}
                  >
                    Phases <Chevron open={openMenu === 'phases'} />
                  </button>
                  {openMenu === 'phases' && (
                    <div className="absolute left-0 mt-2 w-56 rounded-lg shadow-xl overflow-hidden z-[200] border border-white/10">
                      <Link to="/workflows" className={menuItem('from-cyan-600 to-blue-600', 'from-cyan-700 to-blue-700')} onClick={close}>
                        <FiList className="w-4 h-4 shrink-0" /> Browse Phases
                      </Link>
                      <Link to="/workflows/new" className={menuItem('from-blue-600 to-indigo-600', 'from-blue-700 to-indigo-700')} onClick={close}>
                        <FiPlus className="w-4 h-4 shrink-0" /> Create Phase
                      </Link>
                      <Link to="/workflows/timeline" className={menuItem('from-indigo-600 to-purple-600', 'from-indigo-700 to-purple-700', false)} onClick={close}>
                        <FiClock className="w-4 h-4 shrink-0" /> Timeline
                      </Link>
                    </div>
                  )}
                </div>

                {/* ── 4. Artifactory ▾ ─────────────────────────────────── */}
                <div className="relative" ref={artifactoryRef}>
                  <button
                    onClick={() => toggle('artifactory')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center transition-colors hover:bg-white/10 ${isArtifactsActive ? 'text-white bg-white/10' : ''}`}
                  >
                    Artifactory <Chevron open={openMenu === 'artifactory'} />
                  </button>
                  {openMenu === 'artifactory' && (
                    <div className="absolute left-0 mt-2 w-60 rounded-lg shadow-xl overflow-hidden z-[200] border border-white/10">
                      <Link to="/artifactory" className={menuItem('from-indigo-600 to-blue-600', 'from-indigo-700 to-blue-700')} onClick={close}>
                        <FiLayers className="w-4 h-4 shrink-0" /> Blueprints <BetaBadge />
                      </Link>
                      <button
                        onClick={() => { close(); navigate('/artifactory', { state: { startNew: true } }); }}
                        className={menuItem('from-blue-600 to-purple-600', 'from-blue-700 to-purple-700')}
                      >
                        <FiPlus className="w-4 h-4 shrink-0" /> New Blueprint <BetaBadge />
                      </button>
                      <Link to="/artifacts" className={menuItem('from-purple-600 to-indigo-600', 'from-purple-700 to-indigo-700', false)} onClick={close}>
                        <FiArchive className="w-4 h-4 shrink-0" /> Artifacts Index
                      </Link>
                    </div>
                  )}
                </div>

                {/* ── 5. The Board ▾ ──────────────────────────────────── */}
                <div className="relative" ref={boardRef}>
                  <button
                    onClick={() => toggle('board')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center transition-colors hover:bg-white/10 ${isBoardActive ? 'text-blue-300 bg-white/10' : ''}`}
                  >
                    The Board <Chevron open={openMenu === 'board'} />
                  </button>
                  {openMenu === 'board' && (
                    <div className="absolute left-0 mt-2 w-56 rounded-lg shadow-xl overflow-hidden z-[200] border border-white/10">
                      <div className={menuSectionHeader}>Plan</div>
                      <Link to="/work/chat" className={menuItem('from-blue-600 to-indigo-600', 'from-blue-700 to-indigo-700')} onClick={close}>
                        <FiMessageCircle className="w-4 h-4 shrink-0" /> Strategy Chat
                      </Link>
                      <Link to="/goals" className={menuItem('from-indigo-600 to-blue-500', 'from-indigo-700 to-blue-600')} onClick={close}>
                        <FiTarget className="w-4 h-4 shrink-0" /> Goals
                      </Link>
                      <Link to="/projects" className={menuItem('from-blue-500 to-indigo-500', 'from-blue-600 to-indigo-600')} onClick={close}>
                        <FiFolder className="w-4 h-4 shrink-0" /> Projects
                      </Link>
                      <div className={menuSectionHeader}>Execute</div>
                      <Link to="/tasks" className={menuItem('from-violet-600 to-purple-500', 'from-violet-700 to-purple-600')} onClick={close}>
                        <FiCheckSquare className="w-4 h-4 shrink-0" /> Tasks
                      </Link>
                      <Link to="/calendar" className={menuItem('from-purple-500 to-violet-600', 'from-purple-600 to-violet-700', false)} onClick={close}>
                        <FiCalendar className="w-4 h-4 shrink-0" /> Calendar
                      </Link>
                    </div>
                  )}
                </div>

                {/* ── 6. Observatory ▾ ─────────────────────────────────── */}
                <div className="relative" ref={observatoryRef}>
                  <button
                    onClick={() => toggle('observatory')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center transition-colors hover:bg-white/10 ${isObservatoryActive ? 'text-violet-300 bg-violet-500/10' : ''}`}
                  >
                    Observatory <Chevron open={openMenu === 'observatory'} />
                  </button>
                  {openMenu === 'observatory' && (
                    <div className="absolute left-0 mt-2 w-60 rounded-lg shadow-xl overflow-hidden z-[200] border border-white/10">
                      <div className={menuSectionHeader}>Dashboards</div>
                      <Link to="/dashboards" className={menuItem('from-violet-600 to-purple-600', 'from-violet-700 to-purple-700')} onClick={close}>
                        <FiActivity className="w-4 h-4 shrink-0" /> The Pulse
                      </Link>
                      <Link to="/dashboards/personas" className={menuItem('from-purple-600 to-blue-600', 'from-purple-700 to-blue-700')} onClick={close}>
                        <FiUserCheck className="w-4 h-4 shrink-0" /> Forge Monitor
                      </Link>
                      <Link to="/dashboards/workflows" className={menuItem('from-blue-600 to-cyan-600', 'from-blue-700 to-cyan-700')} onClick={close}>
                        <FiGitPullRequest className="w-4 h-4 shrink-0" /> Phase Watch
                      </Link>
                      <Link to="/dashboards/artifactory" className={menuItem('from-cyan-600 to-teal-600', 'from-cyan-700 to-teal-700')} onClick={close}>
                        <FiLayers className="w-4 h-4 shrink-0" /> Blueprint Vault
                      </Link>
                      <Link to="/dashboards/me" className={menuItem('from-sky-600 to-sky-500', 'from-sky-700 to-sky-600')} onClick={close}>
                        <FiBarChart2 className="w-4 h-4 shrink-0" /> My Dashboard
                      </Link>
                      <Link to="/heartbeat" className={menuItem('from-indigo-600 to-indigo-500', 'from-indigo-700 to-indigo-600')} onClick={close}>
                        <FiHeart className="w-4 h-4 shrink-0" /> Heartbeat History
                      </Link>
                      <div className={menuSectionHeader}>Configure</div>
                      <Link to="/tools" className={menuItem('from-gray-600 to-gray-500', 'from-gray-700 to-gray-600')} onClick={close}>
                        <FiTool className="w-4 h-4 shrink-0" /> Tools
                      </Link>
                      <Link to="/skills" className={menuItem('from-gray-500 to-gray-600', 'from-gray-600 to-gray-700', false)} onClick={close}>
                        <FiBookOpen className="w-4 h-4 shrink-0" /> Skills
                      </Link>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <a href="#features" className="px-3 py-1.5 rounded-md text-sm font-medium hover:bg-white/10">Features</a>
                <a href="#how-it-works" className="px-3 py-1.5 rounded-md text-sm font-medium hover:bg-white/10">How it Works</a>
                <a href="#use-cases" className="px-3 py-1.5 rounded-md text-sm font-medium hover:bg-white/10">Use Cases</a>
              </>
            )}
          </nav>

          {/* ── User menu ───────────────────────────────────────────────── */}
          {user ? (
            <div className="relative mr-3" ref={userRef}>
              <button
                onClick={() => toggle('user')}
                className="flex items-center focus:outline-none rounded-full hover:ring-2 hover:ring-white/20 transition-all"
                aria-expanded={openMenu === 'user'}
                aria-haspopup="true"
              >
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.full_name || user.username || 'User'}
                    className="w-8 h-8 rounded-full object-cover border-2 border-blue-500"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium bg-blue-600 text-white">
                    {getInitials()}
                  </div>
                )}
                <FiChevronDown className={`ml-1 w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${openMenu === 'user' ? 'rotate-180' : ''}`} />
              </button>

              {openMenu === 'user' && (
                <div className="absolute right-0 mt-2 w-60 rounded-lg shadow-xl z-[200] border border-white/10 overflow-hidden">
                  {/* Identity */}
                  <div className="px-4 py-3 bg-gradient-to-r from-blue-900/60 to-purple-900/60 border-b border-white/10">
                    <p className="text-sm font-semibold text-white truncate">
                      {user.full_name || user.username || user.email.split('@')[0]}
                    </p>
                    <p className="text-xs truncate text-blue-200/60">{user.email}</p>
                    {user.auth_provider && (
                      <p className="text-[10px] mt-0.5 text-blue-300/40">via {user.auth_provider}</p>
                    )}
                  </div>

                  {/* Quick links */}
                  <Link to="/changelog" className={menuItem('from-blue-600 to-indigo-600', 'from-blue-700 to-indigo-700')} onClick={close}>
                    <FiZap className="w-4 h-4" /> What's New
                    {showWhatsNewBadge && <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />}
                  </Link>
                  <Link to="/docs" className={menuItem('from-emerald-600 to-teal-600', 'from-emerald-700 to-teal-700')} onClick={close}>
                    <FiBookOpen className="w-4 h-4" /> Documentation
                    <span className="ml-auto text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-300">
                      BETA
                    </span>
                  </Link>
                  <Link to="/profile" className={menuItem('from-sky-600 to-blue-600', 'from-sky-700 to-blue-700')} onClick={close}>
                    <FiUser className="w-4 h-4" /> Your Profile
                  </Link>
                  <Link to="/dashboards/me" className={menuItem('from-sky-600 to-sky-500', 'from-sky-700 to-sky-600')} onClick={close}>
                    <FiBarChart2 className="w-4 h-4" /> My Dashboard
                  </Link>
                  <Link to="/settings" className={menuItem('from-gray-600 to-gray-500', 'from-gray-700 to-gray-600')} onClick={close}>
                    <FiSettings className="w-4 h-4" /> Settings
                  </Link>

                  {/* Models */}
                  <div className={menuSectionHeader}>Models</div>
                  <Link to="/models" className={menuItem('from-purple-600 to-indigo-600', 'from-purple-700 to-indigo-700')} onClick={close}>
                    <FiCpu className="w-4 h-4" /> Model Dashboard
                  </Link>
                  <Link to="/models/comparison" className={menuItem('from-indigo-600 to-blue-600', 'from-indigo-700 to-blue-700')} onClick={close}>
                    <FiDollarSign className="w-4 h-4" /> Cost Calculator
                  </Link>
                  <Link to="/models/settings" className={menuItem('from-blue-600 to-blue-500', 'from-blue-700 to-blue-600')} onClick={close}>
                    <FiSliders className="w-4 h-4" /> Default Models
                  </Link>

                  {/* Preferences */}
                  <div className={menuSectionHeader}>Preferences</div>
                  <button
                    onClick={() => { toggleDarkMode(); close(); }}
                    className={menuItem('from-gray-600 to-gray-700', 'from-gray-700 to-gray-800', false)}
                  >
                    {darkMode ? <FiSun className="w-4 h-4" /> : <FiMoon className="w-4 h-4" />}
                    {darkMode ? 'Light Mode' : 'Dark Mode'}
                  </button>

                  {/* Admin (superuser only) */}
                  {user.is_superuser && (
                    <>
                      <div className={menuSectionHeader}>Admin</div>
                      <Link to="/admin" className={menuItem('from-amber-600 to-orange-600', 'from-amber-700 to-orange-700', false)} onClick={close}>
                        <FiShield className="w-4 h-4" /> Admin Dashboard
                      </Link>
                    </>
                  )}

                  {/* Sign out */}
                  <div className="mt-2 mx-2 mb-2">
                    <button
                      onClick={() => { close(); logout(); }}
                      className="w-full text-left px-3 py-2.5 text-sm text-red-400 rounded-md bg-red-900/30 hover:bg-red-900/50 transition-all duration-200 flex items-center gap-2"
                    >
                      <FiLogOut className="w-4 h-4" /> Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={openLoginModal}
              className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Log In
            </button>
          )}

          {/* ── Notifications bell ──────────────────────────────────────── */}
          {user && (
            <>
              <div className="relative mr-2" ref={notificationsRef}>
                <NotificationIndicator onClick={() => { setNotificationsOpen(!notificationsOpen); close(); }} />
                {notificationsOpen && (
                  <div className={`absolute right-0 mt-2 w-96 rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} ring-1 ring-black ring-opacity-5 z-[200]`}>
                    <div className="p-4">
                      <NotificationsHistory
                        onClose={() => setNotificationsOpen(false)}
                        maxHeight="400px"
                        compact={true}
                      />
                      <div className="mt-2 text-center">
                        <button
                          onClick={() => { navigate('/notifications'); setNotificationsOpen(false); }}
                          className={`text-sm ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                        >
                          View All Notifications
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Hamburger removed — items merged into user menu above */}
            </>
          )}
        </div>
      </div>
    </header>
  );
};
